import { Loader2 } from 'lucide-react';
import { normalizeLoginDesign, normalizeImagePlacement } from './loginDesign';

// ── Shared pieces ──────────────────────────────────────────────────────────

function BrandLogo({ branding, className, style }) {
  return (
    <img
      src={branding.logoUrl || '/assets/logo.png'}
      alt={branding.name}
      className={className}
      style={style}
      onError={(e) => { e.target.src = '/assets/logo.png'; }}
    />
  );
}

// Darken a #rrggbb hex toward black by `f` (0..1). Used for the split panel's
// gradient second stop. Non-hex inputs (named colors, rgb()) fall back to a
// deep navy so the gradient still reads.
function darken(hex, f = 0.45) {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex || '');
  if (!m) return '#1e293b';
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - f));
  const g = Math.round(((n >> 8) & 255) * (1 - f));
  const b = Math.round((n & 255) * (1 - f));
  return `rgb(${r}, ${g}, ${b})`;
}

// Neutralize a URL for use inside CSS url("…") — the admin sets this, but strip
// characters that could break out of the declaration regardless.
function cssUrl(u) {
  return String(u || '').replace(/["')\\\n\r]/g, '');
}

/**
 * The sign-in form itself — identical across every design. The surrounding
 * `cardClassName` is what each layout swaps (solid card, transparent panel, or
 * frosted glass). `color` is the instance brand color, used for the button.
 */
export function LoginForm({
  t, color, email, setEmail, password, setPassword,
  rememberMe, setRememberMe, error, loading, onSubmit,
  cardClassName = 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm',
}) {
  const brand = color || '#2563eb';
  const shadow = /^#[0-9a-fA-F]{6}$/.test(brand) ? `${brand}55` : 'rgba(37,99,235,.33)';
  const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all';
  const labelCls = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5';
  return (
    <form onSubmit={onSubmit} className={cardClassName}>
      <div className="space-y-4">
        <div>
          <label className={labelCls}>{t('login.email')}</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            autoComplete="email" className={inputCls} placeholder="admin@example.com" required
          />
        </div>
        <div>
          <label className={labelCls}>{t('login.password')}</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password" className={inputCls} placeholder="••••••••" required
          />
        </div>
      </div>

      <label className="flex items-center gap-2 mt-4 cursor-pointer select-none">
        <input
          type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
          className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500/20"
        />
        <span className="text-sm text-slate-600 dark:text-slate-400">{t('login.rememberMe')}</span>
      </label>

      {error && (
        <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200/60 text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        type="submit" disabled={loading}
        className="w-full mt-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60"
        style={{ background: brand, boxShadow: `0 4px 14px -2px ${shadow}` }}
      >
        {loading && <Loader2 size={16} className="inline-block animate-spin ltr:mr-2 rtl:ml-2" />}
        {loading ? t('login.signingIn') : t('login.signIn')}
      </button>
    </form>
  );
}

// ── Immersive backdrop bits ─────────────────────────────────────────────────

function MapGrid() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <pattern id="login-grid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M48 0H0V48" fill="none" stroke="#94a3b8" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#login-grid)" />
    </svg>
  );
}

const PINS = [
  { top: '18%', left: '16%', c: '#10b981' },
  { top: '30%', right: '20%', c: '#f59e0b' },
  { bottom: '24%', left: '24%', c: '#3b82f6' },
  { bottom: '30%', right: '16%', c: '#ef4444' },
  { top: '52%', left: '48%', c: '#10b981' },
];

function Pins() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {PINS.map((p, i) => (
        <span
          key={i}
          className="absolute rounded-full animate-pulse"
          style={{
            top: p.top, left: p.left, right: p.right, bottom: p.bottom,
            width: 8, height: 8, background: p.c,
            boxShadow: `0 0 12px 3px ${p.c}99`, animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Layouts ─────────────────────────────────────────────────────────────────

function Heading({ t, className }) {
  return (
    <>
      <h1 className={`text-2xl font-bold ${className}`}>{t('login.title')}</h1>
      <p className="text-sm opacity-70 mt-1">{t('login.subtitle')}</p>
    </>
  );
}

function CenteredLayout({ t, branding, form }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 text-slate-900 dark:text-slate-100">
          <BrandLogo branding={branding} className="w-20 h-20 rounded-2xl object-contain mb-4" />
          <Heading t={t} className="text-slate-900 dark:text-slate-100" />
        </div>
        <LoginForm {...form} />
      </div>
    </div>
  );
}

function SplitLayout({ t, branding, form }) {
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-slate-950">
      <div
        className="md:w-1/2 flex flex-col items-center justify-center text-center gap-4 px-8 py-12 text-white"
        style={{ background: `linear-gradient(150deg, ${branding.color || '#2563eb'}, ${darken(branding.color)})` }}
      >
        <BrandLogo branding={branding} className="w-24 h-24 rounded-3xl object-contain bg-white/10 p-3 backdrop-blur-sm" />
        <div>
          <div className="text-2xl font-bold">{branding.name}</div>
          {branding.tagline && <div className="text-sm text-white/80 mt-1.5 max-w-xs mx-auto">{branding.tagline}</div>}
        </div>
      </div>
      <div className="md:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-slate-900 dark:text-slate-100">
            <Heading t={t} className="text-slate-900 dark:text-slate-100" />
          </div>
          <LoginForm {...form} cardClassName="bg-transparent border-0 shadow-none p-0" />
        </div>
      </div>
    </div>
  );
}

const GLASS_CARD = 'bg-white/95 dark:bg-slate-900/85 backdrop-blur-xl border border-slate-200/60 dark:border-white/10 rounded-2xl p-6 shadow-2xl';

function ImmersiveLayout({ t, branding, form }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-slate-950">
      <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 25% 20%, #1e293b 0%, #0b1220 70%)' }} />
      <MapGrid />
      <Pins />
      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-6 text-white">
          <BrandLogo branding={branding} className="w-20 h-20 rounded-2xl object-contain mb-4" style={{ filter: 'drop-shadow(0 4px 14px rgba(0,0,0,.55))' }} />
          <Heading t={t} className="text-white" />
        </div>
        <LoginForm {...form} cardClassName={GLASS_CARD} />
      </div>
    </div>
  );
}

function ImageLayout({ t, branding, form }) {
  const placement = normalizeImagePlacement(branding.loginImagePlacement);
  const img = branding.loginImageUrl;

  if (placement === 'side' && img) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-slate-950">
        <div
          className="md:w-1/2 min-h-[30vh] md:min-h-screen bg-cover bg-center"
          style={{ backgroundImage: `url("${cssUrl(img)}")` }}
          aria-hidden="true"
        />
        <div className="md:w-1/2 flex items-center justify-center p-6">
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center md:items-start mb-6 text-slate-900 dark:text-slate-100">
              <BrandLogo branding={branding} className="w-20 h-20 rounded-2xl object-contain mb-3" />
              <Heading t={t} className="text-slate-900 dark:text-slate-100" />
            </div>
            <LoginForm {...form} cardClassName="bg-transparent border-0 shadow-none p-0" />
          </div>
        </div>
      </div>
    );
  }

  // Full-background (default). Graceful even with no image (dark scrim only).
  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden bg-slate-900">
      {img && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${cssUrl(img)}")` }} aria-hidden="true" />}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/75" />
      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-6 text-white">
          <BrandLogo branding={branding} className="w-20 h-20 rounded-2xl object-contain mb-4" style={{ filter: 'drop-shadow(0 4px 14px rgba(0,0,0,.5))' }} />
          <Heading t={t} className="text-white drop-shadow" />
        </div>
        <LoginForm {...form} cardClassName={GLASS_CARD} />
      </div>
    </div>
  );
}

/** Selects the active layout from the (raw, un-normalized) platform setting. */
export function LoginChrome({ design, branding, t, form }) {
  const d = normalizeLoginDesign(design);
  if (d === 'split') return <SplitLayout t={t} branding={branding} form={form} />;
  if (d === 'immersive') return <ImmersiveLayout t={t} branding={branding} form={form} />;
  if (d === 'image') return <ImageLayout t={t} branding={branding} form={form} />;
  return <CenteredLayout t={t} branding={branding} form={form} />;
}
